import React, { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { MODAL_DIALOG_WIDTH, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { ColumnWidth } from 'modules/core/enums';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { FormRow } from 'modules/core/components';
import { ModalDialog } from 'components/ModalDialog';
import { IField, ISelectOption } from 'modules/core/types';
import { getUser } from 'store/selectors/auth.selector';
import { consumerApi, controlApi } from 'api';

import { INVENTORY_SYNDICATION_PROVIDERS, UNLOCK_INVENTORY_FORM_INITIAL_VALUES } from './consts/consts';
import { FormPanel, SuccessMessage, ConfirmCheckboxesPanel, SuccessSubmitLogo, FormMessage } from './styles';
import { UNLOCK_INVENTORY_FIELDS_NAMES } from './consts/fieldNames.const';
import { UNLOCK_INVENTORY_CONFIRM_CHECKBOXES, UNLOCK_INVENTORY_FIELDS } from './consts/fieldConfig.const';
import { getNormalizedDirty } from '../utils';

interface IUnlockInventoryDialog {
  show: boolean;
  onHide: VoidFunction;
}

export const UnlockInventoryDialog: FC<IUnlockInventoryDialog> = ({ show, onHide }) => {
  const { t } = useTranslation();
  const user = useSelector(getUser);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [dealershipStateOptions, setDealershipStateOptions] = useState<ISelectOption[]>([]);

  const initialValues = useMemo(
    () => ({
      ...UNLOCK_INVENTORY_FORM_INITIAL_VALUES,
      fullName: `${user?.firstName} ${user?.lastName}`,
      email: user?.email || '',
    }),
    [user?.firstName, user?.lastName, user?.email],
  );
  const defaultFormValues = { unlockInventoryForm: initialValues };
  const formMethods = useForm({
    mode: 'onSubmit',
    shouldUnregister: false,
    defaultValues: defaultFormValues,
  });
  const { getValues, reset, handleSubmit } = formMethods;

  const normalizedDirty = getNormalizedDirty(formMethods, defaultFormValues);

  const inventoryProviderOptions: ISelectOption[] = INVENTORY_SYNDICATION_PROVIDERS.map((provider) => ({
    label: provider,
    value: provider,
  }));

  const getDealershipStateOptions = useCallback(async () => {
    const response = await controlApi.getStateRegions();
    const options = response.data.map((state) => ({
      label: state,
      value: state,
    }));

    setDealershipStateOptions(options);
  }, []);

  useEffect(() => {
    getDealershipStateOptions();
  }, [getDealershipStateOptions]);

  const onSubmit = useCallback(() => {
    const values: any = getValues().unlockInventoryForm;

    consumerApi.postUnlockInventory({
      full_name: values.fullName,
      email: values.email,
      phone_number: `+1${values.phoneNumber.toString()}`,
      dealership_name: values.dealershipName,
      dealership_state: values?.dealershipState?.value,
      inventory_syndication_provider: values?.inventorySyndicationProvider?.value,
      credit_application_system: values.creditApplicationSystem,
    });
    setSubmitted(true);
  }, [getValues]);

  useEffect(() => {
    if (show) setSubmitted(false);
  }, [show]);

  const handleHide = useCallback(() => {
    onHide();
    reset({ unlockInventoryForm: initialValues });
  }, [reset, onHide, initialValues]);

  const onSubmitCallback = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const renderFields = useCallback(
    (fields: IField[]) =>
      fields.map(({ name, component: Component, controlProps, label, textLabel }: IField) => {
        if (name === UNLOCK_INVENTORY_FIELDS_NAMES.DEALERSHIP_STATE) {
          controlProps = { ...controlProps, options: dealershipStateOptions };
        } else if (name === UNLOCK_INVENTORY_FIELDS_NAMES.INVENTORY_SYNDICATION_PROVIDER) {
          controlProps = { ...controlProps, options: inventoryProviderOptions };
        }

        return (
          <FormRow
            fieldWidth={controlProps?.fieldWidth as ColumnWidth}
            key={name}
            label={label}
            name={name}
            required={!!controlProps?.required}
            control={<Component {...controlProps} name={name} textLabel={textLabel} />}
          />
        );
      }),
    [dealershipStateOptions, inventoryProviderOptions],
  );

  return (
    <FormProvider {...formMethods}>
      <ModalDialog
        show={show}
        onHide={handleHide}
        modalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_613}
        minModalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_613}
        keyboard={false}
        verticallyCentered={true}
        title={t('components.unlockInventoryDialog.title')}
        showFooter={!submitted}
        cancelButtonTitle={t('components.unlockInventoryDialog.buttons.cancel')}
        submitButtonTitle={t('components.unlockInventoryDialog.buttons.submit')}
        onSubmitModal={onSubmitCallback}
        isDirty={normalizedDirty}
      >
        {submitted ? (
          <FormPanel isSuccessMessage>
            <SuccessMessage>
              <Trans defaults={t('components.unlockInventoryDialog.successSubmit.message')} components={[<br />]} />
            </SuccessMessage>
            <SuccessSubmitLogo
              src={STATIC_IMAGE_URLS.icons.successSubmit}
              alt={t('components.unlockInventoryDialog.successSubmit.logoAlt')}
            />
          </FormPanel>
        ) : (
          <>
            <FormPanel>
              <FormMessage>
                <div>{t('components.unlockInventoryDialog.form.message')}</div>
              </FormMessage>
              {renderFields(UNLOCK_INVENTORY_FIELDS)}
            </FormPanel>
            <ConfirmCheckboxesPanel>{renderFields(UNLOCK_INVENTORY_CONFIRM_CHECKBOXES)}</ConfirmCheckboxesPanel>
          </>
        )}
      </ModalDialog>
    </FormProvider>
  );
};
